import {
  Checkbox,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  RadioGroup,
  Textarea,
  toast,
} from "@/components/ui";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/Dialog";
import { CircleQuestionMark } from "@/components/ui/Icons";
import { Tooltip } from "@/components/ui/Tooltip";
import { TRACKERS } from "@/constants/Trackers";
import { useGetPatient } from "@/features/Patient/network/usePatient";
import { useRoomToken } from "@/features/Patient/network/useRoomToken";
import { isUniversalClinicPatient } from "@/features/Patient/utils";
import { useEnvironment } from "@/hooks/useEnvironment";
import { MessageSeverity, addBreadcrumb, getReplayId } from "@/lib/monitoring";
import { trackEvent } from "@/lib/tracking/tracking";
import { useEncounter } from "@/network/useEncounter";
import { useUser } from "@/network/useUser";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  PatientSource,
  useReportProviderFeedback,
} from "../../network/useProviderFeedback";
import type { PatientDemographics } from "../../types/patientDemographics";
import {
  type FeedbackFormValues,
  FeedbackSchema,
  FeedbackType,
} from "./schema";

interface ProviderFeedbackProps {
  administrative: PatientDemographics["administrative"];
}

export const ProviderFeedback = ({ administrative }: ProviderFeedbackProps) => {
  const [open, setOpen] = useState(false);
  const { environment } = useEnvironment();
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });
  const { data: roomToken } = useRoomToken({
    patientId: patientId as string,
    encounterId: encounterId?.toString(),
  });
  const { data: encounter } = useEncounter(
    encounterId?.toString(),
    patientId as string,
  );
  const { data: user } = useUser();
  const { data: patient } = useGetPatient(patientId as string);

  const patientSource = isUniversalClinicPatient(patient)
    ? PatientSource.UNIVERSAL
    : patient?.amazonId
      ? PatientSource.AMAZON
      : PatientSource.CURAI;

  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(FeedbackSchema),
    defaultValues: {
      title: "",
      description: "",
      feedbackType: FeedbackType.GeneralEHR,
      careBlocking: false,
      reachedOutToCODLead: false,
      availableOnSlack: false,
    },
  });

  const careBlocking = form.watch("careBlocking");

  const { mutate: reportFeedback, isPending } = useReportProviderFeedback({
    patientId,
    onSuccess: (response) => {
      toast.success("Feedback submitted successfully");
      setOpen(false);
      form.reset();

      addBreadcrumb({
        category: "feedback",
        message: "Provider feedback submitted",
        data: {
          url: String(response?.data ?? "missing data"),
        },
        level: MessageSeverity.Error,
      });
    },
    onError: (error) => {
      toast.error(`Failed to submit feedback: ${error.message}`);
    },
  });

  // "User ID" on the ticket: the source-specific id shown in the PatientBar. For Universal/CHAI
  // patients this is the Curai MRN (looks like "U..."); the Clerk id is sent separately as
  // chaiUserId below.
  const userId: string | null =
    patientSource === PatientSource.AMAZON
      ? (patient?.amazonId ?? null)
      : patientSource === PatientSource.UNIVERSAL
        ? administrative.mrn
        : (patient?.userId ?? null);

  // Chai/Nori (Clerk) user id (e.g. "user_..."), the Braintrust metadata.user_id join key to the
  // patient's Nori conversation/trace. Only Universal patients have one.
  const chaiUserId: string | null =
    patientSource === PatientSource.UNIVERSAL
      ? administrative.chaiUserId
      : null;

  const encounterServiceType: string | null =
    patientSource === PatientSource.UNIVERSAL
      ? (encounter?.serviceType?.text ??
        encounter?.serviceType?.coding?.[0]?.display ??
        null)
      : null;

  const onSubmit = (values: FeedbackFormValues) => {
    trackEvent(TRACKERS.SUBMIT_A_BUG_REPORT);
    if (!user) {
      toast.error("Failed to submit feedback; user information not available.");
      return;
    }

    const userIdentifier =
      user.professional?.displayNameInternal || `Provider ID: ${user.userId}`;
    const description = `Reported by ${userIdentifier}\n\n ${values.description}`;
    const title = `[${environment.toUpperCase()}] ${values.title}`;

    reportFeedback({
      title,
      description,
      feedbackType: values.feedbackType,
      careBlocking: values.careBlocking,
      patientId: patientId as string,
      patientSource,
      userId,
      chaiUserId,
      roomToken: roomToken || "",
      providerId: user.userId,
      encounterId: `${encounterId}`,
      replayId: getReplayId(),
      encounterServiceType,
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (open) {
          trackEvent(TRACKERS.START_A_BUG_REPORT);
        }
        setOpen(open);
      }}
    >
      <DialogTrigger asChild>
        <Button variant="icon" className="m-0 p-0">
          <Tooltip
            side="bottom"
            tooltipContentText="Submit feedback about the EMR system"
          >
            <CircleQuestionMark className="h-4 w-4 text-muted-foreground" />
          </Tooltip>
        </Button>
      </DialogTrigger>
      <DialogContent onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Report an Issue</DialogTitle>
          <DialogDescription>
            Please provide details about the issue you're experiencing.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <Controller
            control={form.control}
            name="title"
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="title">Title</FieldLabel>
                <Input
                  {...field}
                  id="title"
                  placeholder="Brief description of the issue"
                  aria-invalid={fieldState.invalid}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
          <Controller
            control={form.control}
            name="description"
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="description">Description</FieldLabel>
                <Textarea
                  {...field}
                  id="description"
                  placeholder="Please provide more details about the issue"
                  className="min-h-[168px]"
                  aria-invalid={fieldState.invalid}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
          <Controller
            control={form.control}
            name="feedbackType"
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="feedbackType">Feedback Type</FieldLabel>
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  options={[
                    {
                      value: FeedbackType.GeneralEHR,
                      label: "General EHR Issues",
                    },
                    {
                      value: FeedbackType.Automation,
                      label: "Automation Issues",
                    },
                  ]}
                  aria-invalid={fieldState.invalid}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
          <div className="text-sm text-muted-foreground">
            Care blocking? Make sure you try hard refreshing (CMD + SHIFT + R
            for Macs) or, for orders, canceling and re-ordering before
            check-marking below.
          </div>
          <Controller
            control={form.control}
            name="careBlocking"
            render={({ field, fieldState }) => (
              <Field
                data-invalid={fieldState.invalid}
                className="flex flex-row items-start space-x-3 space-y-0"
              >
                <Checkbox
                  id="careBlocking"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  aria-invalid={fieldState.invalid}
                />
                <div className="space-y-1 leading-none">
                  <FieldLabel htmlFor="careBlocking">
                    This issue is blocking patient care
                  </FieldLabel>
                  <FieldDescription>
                    I am requesting an engineer to reach out to me within 15
                    minutes to help troubleshoot. I will stay online and keep my
                    Slack notifications on.
                  </FieldDescription>
                </div>
              </Field>
            )}
          />
          {careBlocking && (
            <div className="space-y-4">
              <Controller
                control={form.control}
                name="reachedOutToCODLead"
                render={({ field, fieldState }) => (
                  <Field
                    data-invalid={fieldState.invalid}
                    className="flex flex-row items-start space-x-3 space-y-0"
                  >
                    <Checkbox
                      id="reachedOutToCODLead"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      aria-invalid={fieldState.invalid}
                    />
                    <div className="space-y-1 leading-none">
                      <FieldLabel htmlFor="reachedOutToCODLead">
                        I have reached out to my COD Lead about this issue
                      </FieldLabel>
                      <FieldError errors={[fieldState.error]} />
                    </div>
                  </Field>
                )}
              />
              <Controller
                control={form.control}
                name="availableOnSlack"
                render={({ field, fieldState }) => (
                  <Field
                    data-invalid={fieldState.invalid}
                    className="flex flex-row items-start space-x-3 space-y-0"
                  >
                    <Checkbox
                      id="availableOnSlack"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      aria-invalid={fieldState.invalid}
                    />
                    <div className="space-y-1 leading-none">
                      <FieldLabel htmlFor="availableOnSlack">
                        I will be available on Slack to help troubleshoot
                      </FieldLabel>
                      <FieldError errors={[fieldState.error]} />
                    </div>
                  </Field>
                )}
              />
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                trackEvent(TRACKERS.CANCEL_BUG_REPORT_SUBMISSION);
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
